"use client"
import { useMemo, useRef } from "react"
import type { Globe2Settings } from "../../globe/globe2Types"
import { useHudContext, useHudDates } from "../../hud/shared/playbookHUDInfra"
import { PlaybookGlobe } from "../../PlaybookGlobe"
import {
  COMPACT_STRIP_VARIANTS,
  type MobileStripDesignId,
  type MobileStripVariantConfig,
} from "./stripVariants"

// datacenterFlat mode: no perceptible extrusion — ~0.2px at a 92px earth,
// just enough to lift the tops clear of the coastline layer so they
// don't z-fight invisible. Side faces are hidden but the dark edge
// outlines are KEPT: with sub-pixel verticals they read as flat hex
// outlines, which is what makes the footprints visible at this size.
const MOBILE_STRIP_DATACENTER_FLAT_HEIGHT = 0.008

// The strip's globe as its own absolutely-positioned layer. Normal
// variants render it inside the strip; globeOverflowsCard variants have
// MobileHUD mount it OUTSIDE the clipped card instead, so the earth
// overlaps the page above the strip.
export const MobileStripGlobeLayer = ({
  variant,
  height,
  globePaused,
}: {
  variant: MobileStripDesignId
  // The strip height the globe positions against.
  height: number
  globePaused: boolean
}) => {
  const config = COMPACT_STRIP_VARIANTS[variant]
  const ctx = useHudContext()
  const hudDates = useHudDates()
  const globeDate = useFrozenWhilePaused(hudDates.animatedDate, globePaused)
  const globeSettings = useMemo(
    () => ctx.globeSettings
      ? flattenGlobeSettingsForStrip(ctx.globeSettings, config)
      : ctx.globeSettings,
    [ctx.globeSettings, config],
  )
  const globeTop = config.globeTopOffsetPx ?? (height - config.globeSizePx) / 2

  return (
    <div
      className="pointer-events-none absolute transition-opacity duration-[600ms] ease-in-out"
      style={{
        left: config.globeLeftPx,
        top: globeTop,
        // While the choose-your-path block is on screen the earth fades
        // out so the all-plans fan reads unobstructed — the mobile mirror
        // of the desktop globe fade, on its 600ms galaxy-hide timing. The
        // MobileHUD reveal/morph crossfades ride on the wrapper above
        // this, so they don't fight this opacity.
        opacity: ctx.isChoicePointOnScreen ? 0 : 1,
      }}
    >
      <PlaybookGlobe
        currentDate={globeDate}
        activePlan={ctx.activePlan}
        globeSettings={globeSettings}
        countryWakeup={hudDates.countryWakeup}
        stateWakeup={hudDates.stateWakeup}
        legendMarkerPositions={ctx.legendMarkerPositions}
        size={config.globeSizePx}
        lockSizeToInitialHeight={false}
        minDevicePixelRatio={2}
        paused={globePaused}
        isHoverPreviewActive={ctx.isHoverPreviewActive}
      />
    </div>
  )
}

// Freeze the globe date while paused (HudGlobeBlock's trick) so the
// memoized globe skips per-frame re-renders while the strip is hidden.
const useFrozenWhilePaused = (animatedDate: Date, paused: boolean): Date => {
  const lastUnpausedRef = useRef(animatedDate)
  if (!paused) lastUnpausedRef.current = animatedDate
  return paused ? lastUnpausedRef.current : animatedDate
}

// Flatten datacenters to surface footprints and thin the globe's
// linework (sphere rim, coastlines, datacenter edges) per the variant.
const flattenGlobeSettingsForStrip = (
  settings: Globe2Settings,
  config: MobileStripVariantConfig,
): Globe2Settings => ({
  ...settings,
  sphereSettings: thinnedSphere(settings.sphereSettings, config),
  ...(settings.continentSettings
    ? { continentSettings: simplifiedContinents(settings.continentSettings, config) }
    : {}),
  ...(settings.rectDatacenterSettings
    ? { rectDatacenterSettings: flattenedRectDatacenters(settings.rectDatacenterSettings, config) }
    : {}),
  // The active production globe variant draws its datacenters with the
  // HEX layer, not the rect one — flatten it identically.
  ...(settings.hexDatacenterSettings
    ? { hexDatacenterSettings: flattenedHexDatacenters(settings.hexDatacenterSettings, config) }
    : {}),
})

type SphereSettings = Globe2Settings["sphereSettings"]
type ContinentSettings = NonNullable<Globe2Settings["continentSettings"]>
type RectDatacenterSettings = NonNullable<Globe2Settings["rectDatacenterSettings"]>
type HexDatacenterSettings = NonNullable<Globe2Settings["hexDatacenterSettings"]>

const thinnedSphere = (
  sphere: SphereSettings,
  config: MobileStripVariantConfig,
): SphereSettings => ({
  ...sphere,
  outlineWidth: config.globeSphereOutlineWidthPx
    ?? sphere.outlineWidth * config.globeLineWidthScale,
})

const simplifiedContinents = (
  continents: ContinentSettings,
  config: MobileStripVariantConfig,
): ContinentSettings => ({
  ...continents,
  coastlineWidth: continents.coastlineWidth * config.globeLineWidthScale,
  ...(config.coastlineSimplifyDeg !== null
    ? { coastlineSimplifyToleranceDeg: config.coastlineSimplifyDeg }
    : {}),
})

// Flat mode collapses the towers to footprints (side faces hidden, edge
// outlines kept); otherwise the strip's own height envelope applies.
// Footprints widen either way so the sites read at strip size.
const flattenedRectDatacenters = (
  rect: RectDatacenterSettings,
  config: MobileStripVariantConfig,
): RectDatacenterSettings => ({
  ...rect,
  minHeight: config.datacenterFlat ? MOBILE_STRIP_DATACENTER_FLAT_HEIGHT : rect.minHeight,
  maxHeight: config.datacenterFlat
    ? MOBILE_STRIP_DATACENTER_FLAT_HEIGHT
    : config.datacenterMaxHeightFrac,
  ...(config.datacenterFlat ? {} : { heightPower: config.datacenterHeightPower }),
  minWidth: rect.minWidth * config.datacenterWidthScale,
  maxWidth: rect.maxWidth * config.datacenterWidthScale,
  edgeWidth: rect.edgeWidth * config.globeLineWidthScale,
  ...(config.datacenterFlat ? { faceOpacity: 0 } : {}),
})

// Same flattening for the hex layer; flat mode also neutralizes the
// satellite height multipliers, which scale prism heights back up.
const flattenedHexDatacenters = (
  hex: HexDatacenterSettings,
  config: MobileStripVariantConfig,
): HexDatacenterSettings => ({
  ...hex,
  minHeight: config.datacenterFlat ? MOBILE_STRIP_DATACENTER_FLAT_HEIGHT : hex.minHeight,
  maxHeight: config.datacenterFlat
    ? MOBILE_STRIP_DATACENTER_FLAT_HEIGHT
    : config.datacenterMaxHeightFrac,
  ...(config.datacenterFlat
    ? { satelliteHeightMultiplier: 1, thirdRingSatelliteHeightMultiplier: 1 }
    : { heightPower: config.datacenterHeightPower }),
  minWidth: hex.minWidth * config.datacenterWidthScale,
  maxWidth: hex.maxWidth * config.datacenterWidthScale,
  edgeWidth: hex.edgeWidth * config.globeLineWidthScale,
  ...(config.datacenterFlat ? { faceOpacity: 0 } : {}),
})
